/**
 * SKIN-242 — what the rail says about a visit, derived from the marks below it.
 *
 * Nothing here is stored. Progress is a projection of the per-image assessments and the
 * per-lesion feedback the container already holds, so the rail and the cards can never
 * report different totals — which is the failure mode a cached count would produce, and
 * the one a reviewer would trust least.
 */
import { ImageAssessmentValue } from './ImageAssessment';
import { LesionFeedbackValue } from './LesionPanel';
import { ReviewImage, dedupeImagesByFile, isCurtained } from './imageMeta';

export interface VisitReviewProgress {
    /** Photographs on this visit, after duplicate eCRF records are collapsed. */
    total: number;
    /** How many of them carry a mark. */
    assessed: number;
    /** Every photograph is marked. Approval is refused until this is true. */
    complete: boolean;
    /**
     * The reviewer has said something the field team will receive — any image marked
     * *Needs feedback*, or any lesion panel with a category or a note.
     *
     * This is what relabels the primary action, so it deliberately counts a bare `needs`
     * with no category and no comment: the mark alone is already a message back.
     */
    hasFeedback: boolean;
    /**
     * How many PHOTOGRAPHS went back marked *Needs feedback*.
     *
     * Derived here, from the same `outcomes` array as `hasFeedback`, rather than left for a
     * caller to count. The two answer different questions — a lesion's missing macro shot is
     * feedback with no photograph to be counted against, so this can be 0 while
     * `hasFeedback` is true — and computing them apart is what let a screen print "3 returned
     * with feedback" and "No feedback was sent" at once. Same source, one dedupe, one pass.
     */
    withFeedback: number;
    /** Photographs covered by the identifiable curtain. */
    curtained: number;
}

/**
 * Count the visit.
 *
 * **The count is over the deduplicated list**, the same collapse the gallery does. In
 * production one photograph appears in about five `survey_record` rows, so counting rows
 * would ask for roughly five times as many marks as there are cards — and since a card can
 * only be marked once, "X of Y" would never reach Y and the visit could never be approved.
 */
export function computeVisitProgress({
    images,
    assessmentFor,
    lesionFeedback,
}: {
    images: ReviewImage[];
    assessmentFor: (encryptedFileUuid: string) => ImageAssessmentValue;
    /** Every lesion panel's value on this visit. Which lesion each belongs to is not the rail's business. */
    lesionFeedback: LesionFeedbackValue[];
}): VisitReviewProgress {
    const distinct = dedupeImagesByFile(images);

    const outcomes = distinct.map((image) => assessmentFor(image.encryptedFileUuid).outcome);
    const assessed = outcomes.filter((outcome) => outcome !== null).length;

    const lesionHasFeedback = lesionFeedback.some(
        (value) => value.categoryIds.length > 0 || Boolean(value.comment.trim()),
    );

    return {
        total: distinct.length,
        assessed,
        // A visit with no images is complete rather than permanently blocked. The
        // alternative — `total > 0 && …` — leaves the reviewer staring at a banner reading
        // "still unmarked: 0" with no action that could ever clear it. An empty visit is a
        // data problem, and the review screen is not where it gets fixed.
        complete: assessed === distinct.length,
        hasFeedback: outcomes.includes('needs') || lesionHasFeedback,
        withFeedback: outcomes.filter((outcome) => outcome === 'needs').length,
        curtained: distinct.filter(isCurtained).length,
    };
}
